"use client";

import { useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useOpenCapture } from "@/hooks/use-open-capture";
import { DropdownMenu, DropdownMenuHeader, DropdownMenuDivider } from "@speakai/ui/dropdown-menu";
import {
  Plus,
  FileUp,
  Globe,
  AudioLines,
  CalendarClock,
  Mic,
  Zap,
  FileSpreadsheet,
} from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { topbarPhoneGhostClasses } from "@/components/layout/balance-button";
import { track } from "@/lib/integrations/sentry-tracking";
import { trackQuickAction } from "@/lib/integrations/ga4";
import { useTranslation } from "react-i18next";
import { formatQuickActionShortcut } from "@/lib/utils/format-shortcut";
import { useAccountUsage } from "@/features/dashboard/queries";
import { canImportCSV } from "@/features/subscription/lib/feature-gates";
import { useAuth } from "@/hooks/use-auth";
import { getQuickActionRecorderLabelKey } from "@/features/onboarding/bucket-labels";

interface QuickAction {
  id: string;
  labelKey: string;
  label: string;
  icon: React.ElementType;
  group: "capture" | "automate";
  route?: string;
  shortcutLetter?: string;
}

const QUICK_ACTIONS: QuickAction[] = [
  { id: "upload", labelKey: "QUICK_ACTION.NEW_UPLOAD", label: "Upload", icon: FileUp, group: "capture", route: "/upload", shortcutLetter: "U" },
  { id: "translate", labelKey: "QUICK_ACTION.TRANSLATION_NOUN", label: "Translation", icon: Globe, group: "capture", route: "/translate", shortcutLetter: "L" },
  { id: "record", labelKey: "QUICK_ACTION.RECORDING_NOUN", label: "Recording", icon: AudioLines, group: "capture", route: "/upload/record", shortcutLetter: "E" },
  { id: "join-meeting", labelKey: "QUICK_ACTION.MEETING_NOUN", label: "Meeting", icon: CalendarClock, group: "capture", route: "/meeting-assistant?dialog=join_meeting", shortcutLetter: "M" },
  { id: "new-survey", labelKey: "QUICK_ACTION.NEW_SURVEY", label: "Survey", icon: Mic, group: "capture", route: "/recorder/create", shortcutLetter: "S" },
  { id: "automations", labelKey: "QUICK_ACTION.AUTOMATION_NOUN", label: "Automation", icon: Zap, group: "automate", route: "/automations" },
  { id: "import-csv", labelKey: "QUICK_ACTION.CSV_NOUN", label: "CSV", icon: FileSpreadsheet, group: "automate", route: "/home?dialog=import-csv" },
];


interface QuickActionMenuProps {
  collapsed?: boolean;
  folderId?: string;
  variant?: "primary" | "outline";
  triggerLabel?: string;
  hideLabelOnMobile?: boolean;
}

export function QuickActionMenu({ collapsed, folderId, variant = "primary", triggerLabel, hideLabelOnMobile = false }: QuickActionMenuProps = {}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const openCapture = useOpenCapture();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const currentFolderId = folderId || pathname.match(/^\/folder\/([^/?]+)/)?.[1];

  const { data: usage } = useAccountUsage();
  const csvImportAllowed = canImportCSV(usage?.features) && !usage?.isExpired;

  const recorderLabel = getQuickActionRecorderLabelKey(user?.onboarding?.goal);
  const captureActions = QUICK_ACTIONS.filter((a) => a.group === "capture").map((a) =>
    a.id === "new-survey" ? { ...a, labelKey: recorderLabel.key, label: recorderLabel.fallback } : a
  );
  const automateActions = QUICK_ACTIONS.filter(
    (a) => a.group === "automate" && (a.id !== "import-csv" || csvImportAllowed),
  );

  const handleAction = useCallback(
    (action: QuickAction) => {
      track("dashboard", "quick_action", { action_type: action.id });
      trackQuickAction(action.id);

      if (action.id === "upload") track("sidebar", "upload");
      else if (action.id === "record") track("sidebar", "record");

      setOpen(false);

      if (action.id === "upload" || action.id === "translate") {
        openCapture(action.id === "translate" ? "translate" : "upload", currentFolderId);
        return;
      }

      if (action.route) {
        const isFolderScoped = currentFolderId && action.id === "record";
        navigate(isFolderScoped ? `${action.route}?folderId=${encodeURIComponent(currentFolderId)}` : action.route);
      }
    },
    [navigate, currentFolderId, openCapture]
  );

  const renderGroup = (label: string, actions: QuickAction[]) => {
    if (actions.length === 0) return null;
    return (
      <>
        <DropdownMenuHeader>{label}</DropdownMenuHeader>
        <div className="flex flex-col px-1 pb-1">
          {actions.map((action) => {
            const shortcut = action.shortcutLetter
              ? formatQuickActionShortcut(action.shortcutLetter)
              : undefined;
            const actionLabel = t(action.labelKey, action.label);
            return (
              <button
                key={action.id}
                type="button"
                role="menuitem"
                onClick={() => handleAction(action)}
                data-testid={`quick-action-menu-${action.id}`}
                title={shortcut ? `${actionLabel} (${shortcut})` : actionLabel}
                className="group flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-popover-foreground hover:bg-muted focus:bg-muted focus:outline-hidden transition-colors"
              >
                <action.icon aria-hidden="true" className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="flex-1 text-left">{actionLabel}</span>
                {shortcut && (
                  <kbd className="hidden sm:inline-flex ml-auto pointer-events-none select-none items-center gap-0.5 rounded border border-border bg-muted px-1.5 py-0.5 text-[11px] font-medium leading-none tracking-wide text-muted-foreground group-hover:text-foreground group-hover:border-muted-foreground/30 shrink-0">
                    {shortcut}
                  </kbd>
                )}
              </button>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={setOpen}
      trigger={
        collapsed ? (
          <button
            type="button"
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            aria-label={t("DASHBOARD.QUICK_ACTIONS")}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            className={cn(
              "inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              variant === "outline"
                ? cn("min-h-10 min-w-10 pointer-coarse:min-h-11 pointer-coarse:min-w-11 border border-border bg-background text-foreground hover:bg-muted", topbarPhoneGhostClasses)
                : "bg-primary text-primary-foreground hover:bg-primary/90"
            )}
            aria-label={t("DASHBOARD.QUICK_ACTIONS")}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            <span className={hideLabelOnMobile ? "hidden sm:inline" : undefined}>{triggerLabel ?? t("ACTION.NEW", "New")}</span>
          </button>
        )
      }
      align="right"
      width="w-72"
    >
      {renderGroup(t("DASHBOARD.CAPTURE"), captureActions)}
      {captureActions.length > 0 && automateActions.length > 0 && <DropdownMenuDivider />}
      {renderGroup(t("DASHBOARD.AUTOMATE"), automateActions)}
    </DropdownMenu>
  );
}
